import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { setTokens, clearTokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(sessionStorage.getItem("gms_location") || "Focal Point");
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
      clearTokens();
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password, loc) => {
    const { data } = await api.post("/auth/login", { email, password, location: loc });
    // Store tokens in memory for Bearer auth (works cross-origin)
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    sessionStorage.setItem("gms_location", loc);
    setLocation(loc);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      // Non-fatal: server-side session/token invalidation may already be gone.
      // Log for observability but still proceed to clear client state.
      if (process.env.NODE_ENV !== "production") {
        console.debug("Logout request failed (clearing client state anyway):", err?.message || err);
      }
    }
    clearTokens();
    setUser(null);
  }, []);

  const updateLocation = useCallback((loc) => {
    sessionStorage.setItem("gms_location", loc);
    setLocation(loc);
  }, []);

  const ctxValue = useMemo(() => (
    { user, location, loading, login, logout, updateLocation }
  ), [user, location, loading, login, logout, updateLocation]);

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
